import { Amount } from '@cart-ui/Amount/Amount'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { ButtonBase } from '@pets-ui/ButtonBase/ButtonBase'
import { CartOutlined } from '@pets-ui/Icons'
import { Image } from '@pets-ui/Image/Image'
import { PlaceholderNoImage } from '@pets-ui/Placeholder/PlaceholderNoImage'
import { Skeleton } from '@pets-ui/Skeleton/Skeleton'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { PRODUCT_CARD_BADGE, type ProductCardBadgeType, type ProductCardEntry } from '../types/product-card-entry'
import { SubtitleChipsRow } from './SubtitleChipsRow'

const CARD_WIDTH = 172

const badgeStyleMap: Record<ProductCardBadgeType, { backgroundColor: string; label: string }> = {
  [PRODUCT_CARD_BADGE.NEW]: { backgroundColor: colors.violet600, label: 'new' },
  [PRODUCT_CARD_BADGE.SALE]: { backgroundColor: colors.red600, label: 'sale' },
}

const styles = stylex.create({
  card: {
    width: CARD_WIDTH,
  },
  imageBox: {
    height: CARD_WIDTH,
    width: CARD_WIDTH,
  },
  ribbon: {
    position: 'absolute',
    transform: 'rotate(-45deg) translate(-25%, -50%)',
    zIndex: 1,
    left: 0,
    right: '60%',
    top: 0,
  },
})

interface ProductCardProps {
  entry: ProductCardEntry
  onSelect: (entry: ProductCardEntry) => void
  /** 若為 true，強制隱藏 new/sale ribbon。例如熱銷區改顯示 rank badge。 */
  hideBadge?: boolean
}

export const ProductCard = (props: ProductCardProps) => {
  const { entry, onSelect, hideBadge } = props
  const badgeStyle = !hideBadge && entry.badge ? badgeStyleMap[entry.badge] : undefined

  return (
    <VStack
      backgroundColor={colors.white}
      borderWidth={1}
      borderStyle="solid"
      borderColor={colors.grey200}
      borderRadius={4}
      overflow="hidden"
      flexShrink={0}
      rootStyle={styles.card}
    >
      <ButtonBase position="relative" overflow="hidden" rootStyle={styles.imageBox} onClick={() => onSelect(entry)}>
        {badgeStyle && (
          <Box backgroundColor={badgeStyle.backgroundColor} rootStyle={styles.ribbon}>
            <Text fontSize={16} align="center" color={colors.white}>{badgeStyle.label}</Text>
          </Box>
        )}
        <Image
          src={entry.imageUrl}
          alt={entry.title}
          width="100%"
          height="100%"
          objectFit="cover"
          loading="lazy"
          loadingFallback={<Skeleton width="100%" height="100%" />}
          fallback={<PlaceholderNoImage width="100%" height="100%" noBorder />}
        />
      </ButtonBase>
      <VStack p={2} gap={1} flex={1}>
        <Box minHeight={40}>
          <Text lineClamp={2} color={colors.grey900}>{entry.title}</Text>
        </Box>
        {entry.subtitleChips && entry.subtitleChips.length > 0 && (
          <SubtitleChipsRow entryKey={entry.key} chips={entry.subtitleChips} />
        )}
        <Amount amount={entry.price} color={colors.red600} isBold />
        <Button
          fullWidth
          color="red"
          size="small"
          startIcon={<CartOutlined />}
          onClick={() => onSelect(entry)}
        >
          加入購物車
        </Button>
      </VStack>
    </VStack>
  )
}
